import type { AxiosError, AxiosRequestConfig, AxiosRequestTransformer } from 'axios';
import apiClient from '@/api/_axios';
import type { ApiResult, DesktopFeatures } from './types';

// HTTP half of the Community bridge (no socket here, so the side menu and the
// header can read the feature switches without loading socket.io).

/* ── HTTP ───────────────────────────────────────────────────────────────── */

/**
 * Chat, marketplace and the feature switches are never reachable while
 * helping another account (server: team route class 'never') — a helper uses
 * Community as themselves. So these calls never carry X-Act-As. The request
 * interceptor adds it before this transformer runs, so it is removed here.
 */
const dropActAs: AxiosRequestTransformer = (data, headers) => {
   headers?.delete?.('X-Act-As');
   return data;
};
export const ownAccount = (): AxiosRequestConfig => {
   const defaults = apiClient.defaults?.transformRequest;
   const rest = Array.isArray(defaults) ? defaults : defaults ? [defaults] : [];
   return { transformRequest: [dropActAs, ...rest], timeout: 20_000 };
};

/** Same shape SmartFi Desktop's main process answered with. */
function failure(e: unknown): ApiResult<never> {
   const err = e as AxiosError<{ error?: string; err?: string; msg?: string }>;
   const status = err?.response?.status;
   if (!status) return { ok: false, code: 'OFFLINE', message: 'Could not reach SmartFi. Check your internet.' };
   if (status === 401 || status === 403) {
      const b = err.response?.data;
      // A 403 with a reason (e.g. "Not available while helping another account") is not an expired sign-in.
      const msg = b?.error || b?.err || b?.msg;
      if (status === 403 && msg && !/token|sign|auth/i.test(msg)) return { ok: false, code: 'HTTP', message: msg, status };
      return { ok: false, code: 'SESSION_EXPIRED', message: 'Your sign-in expired. Sign in again.', status };
   }
   const b = err.response?.data;
   const msg = (b && typeof b === 'object' && (b.error || b.err || b.msg)) || `HTTP ${status}`;
   return { ok: false, code: 'HTTP', message: msg, status };
}

export async function call<T>(fn: () => Promise<T>): Promise<ApiResult<T>> {
   try {
      return { ok: true, data: await fn() };
   } catch (e) {
      if (e instanceof BridgeError) return { ok: false, code: 'HTTP', message: e.message };
      return failure(e);
   }
}
export class BridgeError extends Error {}

export const get = async (path: string) => (await apiClient.get(path, ownAccount())).data;
export const send = async (path: string, body: unknown, method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST') =>
   (await apiClient.request({ ...ownAccount(), url: path, method, data: body ?? {} })).data;

/** The SmartFi Desktop feature switches. An older server without them (404) has none of these features. */
export async function fetchDesktopFeatures(): Promise<ApiResult<DesktopFeatures>> {
   const r = await call(() => get('/desktop/features'));
   if (!r.ok && r.status === 404) return { ok: true, data: { features: {}, seesAll: false } };
   return r as ApiResult<DesktopFeatures>;
}
